import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useFormik } from 'formik';

import { errorToastMessage } from '../../../components/layout/toast-root/toast';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import {
  ApiResponseCache,
  getTrainingExercisesCacheKey,
} from '../../../redux/api-response-cache';
import { useGetFilePaths } from '../../../redux/api/appConfigApi';
import { resetExerciseApiState } from '../../../redux/api/exercisesAPI';
import {
  resetTrainingsApiState,
  useFinishTraining,
  useGetTrainingById,
} from '../../../redux/api/trainingsAPI';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { FileVisibility, TrainingStatus } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { IAppFile } from '../../../types/interfaces/app-file';
import { INewFile } from '../../../types/interfaces/new-file';
import { calculateTrainingTime } from '../../../utils/datetime/date-time-utils';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../utils/files/firebase-storage-utils';
import { getFinishingTrainingValidation } from './FormikData';

type FinishAction = 'normal' | 'update' | 'create';

export function useFinishingTrainingPage() {
  const { trainingId } = useParams<{ trainingId: string }>();
  const { navigate } = useNavigationStack();
  const [finishConfirm, setFinishConfirm] = useState<FinishAction | null>(null);
  const [showCreateProgramModal, setShowCreateProgramModal] = useState(false);
  const {
    data: training,
    isLoading: isTrainingLoading,
    isFetching: isTrainingFetching,
    isError: isTrainingError,
    error: trainingError,
    refetch,
  } = useGetTrainingById(trainingId, {
    skip: !trainingId,
  });
  const [finishTrainingApi] = useFinishTraining();
  const [filesToUpload, setFilesToUpload] = useState<INewFile[]>([]);
  const { t } = useTranslation();
  const { data: user } = useGetMyDetails();
  const { data: filePaths } = useGetFilePaths();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<FinishAction | null>(null);
  const dispatch = useDispatch();
  const trainingTime = useMemo(() => {
    return calculateTrainingTime(training);
  }, [training]);

  useEffect(() => {
    if (!trainingId) {
      navigate(Paths.trainings.toList, { replace: true });
    }
  }, [trainingId, navigate]);

  useEffect(() => {
    if (!training || !user) {
      return;
    }
    if (training.status === TrainingStatus.notStarted) {
      navigate(Paths.trainings.toViewId(training.id), { replace: true });
    } else if (training.status === TrainingStatus.finished) {
      navigate(Paths.trainings.toFinished(training.id), { replace: true });
    } else if (!training.permissions?.canFinish) {
      navigate(Paths.trainings.toViewId(training.id), { replace: true });
    }
  }, [training, user, navigate]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getFinishingTrainingValidation(t),
    initialValues: {
      startDate: training?.startDate || null,
      comment: training?.comment || '',
      finishDate: null,
    },
    onSubmit: (values) => onSubmit(values, { mode: undefined }, 'normal'),
  });

  async function onSubmit(
    values,
    body:
      | {
          mode: 'create' | 'update' | undefined;
          title?: string;
          programCollectionId?: string;
        }
      | undefined,
    action: FinishAction,
  ) {
    if (!user || !filePaths || !user.uid || !user.id) {
      return;
    }

    setIsLoading(true);
    setLoadingAction(action);
    const uploadedStoragePaths: string[] = [];
    let hasError = false;

    try {
      const uploadedFiles: IAppFile[] = await Promise.all(
        filesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.trainings,
            user.uid,
            user.id,
            FileVisibility.public,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);

          return uploadedFile;
        }),
      );

      await finishTrainingApi({
        id: training.id,
        body: {
          comment: values.comment,
          startDate: values.startDate,
          finishDate: values.finishDate,
          filesToUpload: uploadedFiles,
          ...(body || {}),
        },
      }).unwrap();

      ApiResponseCache.remove(getTrainingExercisesCacheKey(training.id));
      dispatch(resetTrainingsApiState());
      dispatch(resetExerciseApiState());
      navigate(Paths.trainings.toList, {
        replace: true,
      });
    } catch (error) {
      hasError = true;
      console.error(
        'Error uploading files to Firebase Storage or finishing training:',
        error,
      );
      errorToastMessage(t('messages.uploadOrSaveFailed'));
    } finally {
      if (hasError && uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
      setIsLoading(false);
      setLoadingAction(null);
    }
  }

  function onFilesToUploadChanged(files: INewFile[]) {
    setFilesToUpload(files);
  }

  function onFinishTraining() {
    return onSubmit(formik.values, undefined, 'normal');
  }

  function onUpdateProgram() {
    return onSubmit(formik.values, { mode: 'update' }, 'update');
  }

  function onCreateProgram(title: string, programCollectionId: string) {
    return onSubmit(
      formik.values,
      {
        mode: 'create',
        title: title,
        programCollectionId: programCollectionId,
      },
      'create',
    );
  }

  return {
    isTrainingLoading,
    isTrainingFetching,
    isTrainingError,
    trainingError,
    refetchTraining: refetch,
    trainingTime,
    isLoading,
    loadingAction,
    formik,
    finishConfirm,
    setFinishConfirm,
    showCreateProgramModal,
    setShowCreateProgramModal,
    training,
    onFilesToUploadChanged,
    onFinishTraining,
    onUpdateProgram,
    onCreateProgram,
  };
}
